import React from 'react';
import MakeAsyncFunction from 'react-redux-promise-listener';
import { withRouter } from 'react-router-dom';
import { Field, Form as FForm } from 'react-final-form';
import { Row, Col, Form as AForm, Switch, Button } from 'antd';
import { CheckCircleTwoTone, CloseCircleTwoTone, PlusCircleFilled } from '@ant-design/icons';
import { promiseListener } from '../../../store';
import { EmailInputControl, InputControl, PasswordControl, UploadProfilePictureControl } from '../../fields/Fields';
import { StyledTitle as Title } from '../../fields/Title';
import * as validations from '../../../utils/validations';
import { NewButton, DoneButton } from '../../../styles/StyledComponents';

import Auditory from '../../../assets/enrollment/auditory.svg';
import ReadingIcon from '../../../assets/enrollment/reading.svg';
import HandsOn from '../../../assets/enrollment/hands-on.svg';
import VisualIcon from '../../../assets/enrollment/visual.svg';
import { capitalize } from '../../fields/renderFields';
import { EDIT_TEACHER_FAILURE, EDIT_TEACHER_REQUEST, EDIT_TEACHER_SUCCESS } from '../../../modules/teachers/redux';
import { FormattedMessage, useIntl } from 'react-intl';

function onSwitch (checked) {
  console.log(`switch to ${checked}`);
}

const ProfileForm = ({ isLoading, teacher }) => {
  const intl = useIntl();
  return (
    <MakeAsyncFunction
      listener={promiseListener}
      start={EDIT_TEACHER_REQUEST}
      resolve={EDIT_TEACHER_SUCCESS}
      reject={EDIT_TEACHER_FAILURE}
    >
      {onSubmit => (
        <FForm
          initialValues={teacher}
          onSubmit={onSubmit}
          render={({
            pristine,
            submitError,
            handleSubmit,
            submitting,
            hasValidationErrors,
            hasSubmitErrors,
            dirtySinceLastSubmit,
            form
          }) => {
            return (
              <div>
                {isLoading && <div>Loading...</div>}
                {submitError && <div className='error'>{submitError}</div>}
                <AForm onFinish={handleSubmit}>
                  <Row gutter={16} justify='center' style={{ width: '100%' }}>
                    <Col>
                      <Title level={3}>
                        <FormattedMessage id="profile.main.title"/>
                      </Title>
                    </Col>
                    <Col flex={1}>
                      <NewButton>
                        <FormattedMessage id="profile.main.share-button"/>
                      </NewButton>
                    </Col>
                  </Row>
                  <br />
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col sm={20} xl={8}>
                      <Row style={{ marginBottom: '20px' }}>
                        <Field
                          name='avatar'
                          component={(props) =>
                            <UploadProfilePictureControl {...props} title={intl.formatMessage({ id: 'profile.edit-profile.editProfile' })} />}
                          tooltip='Profile Picture'
                          level={3}
                        />
                      </Row>
                      <Row gutter={[16, 16]}>
                        <Col>
                          {teacher.emailVerified ? <CheckCircleTwoTone twoToneColor='#52c41a' />
                            : <CloseCircleTwoTone twoToneColor='#eb2f96' />}
                          <small><FormattedMessage id="profile.main.emailVerified"/></small>
                        </Col>
                        <Col>
                          {teacher.isOnboarded ? <CheckCircleTwoTone twoToneColor='#52c41a' />
                            : <CloseCircleTwoTone twoToneColor='#eb2f96' />}
                          <small><FormattedMessage id="profile.main.onboarded"/></small>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    {/* EMAIL INPUT */}
                    <Col sm={20} xl={8}>
                      <small style={{ display: 'flex', justifyContent: 'left' }}><FormattedMessage id="profile.main.firstName"/></small>
                      <Field
                        name='firstName'
                        component={InputControl}
                        type='text'
                        parse={value => value && value.toLowerCase()}
                        format={capitalize}
                        validate={validations.composeValidators(
                          validations.required,
                          validations.minLength(1),
                          validations.maxLength(20)
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    <Col sm={20} xl={8}>
                      <small style={{ display: 'flex', justifyContent: 'left' }}><FormattedMessage id="profile.main.lastName"/></small>
                      <Field
                        name='lastName'
                        component={InputControl}
                        type='text'
                        parse={value => value && value.toLowerCase()}
                        format={capitalize}
                        validate={validations.composeValidators(
                          validations.required,
                          validations.minLength(1),
                          validations.maxLength(20)
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    {/* EMAIL INPUT */}
                    <Col sm={20} xl={8}>
                      <small><FormattedMessage id="profile.main.email"/></small>
                      <Field
                        name='email'
                        component={EmailInputControl}
                        submitError={submitError}
                        type='text'
                        validate={validations.composeValidators(
                          validations.required,
                          validations.email
                        )}
                      />
                    </Col>
                  </Row>
                  <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                    {/* Password INPUT */}
                    <Col sm={20} xl={8}>
                      <small><FormattedMessage id="profile.main.password"/></small>
                      <Field
                        name='password'
                        component={PasswordControl}
                        type='text'
                        initialValue='********'
                      />
                    </Col>
                  </Row>
                  <br />
                  {/* LEARNER TYPES SECTION */}
                  <Row>
                    <Col sm={20} xl={8}>
                      <strong><FormattedMessage id="profile.main.learning-styles.title"/></strong>
                    </Col>
                    <Col xl={16} />
                    <Col>
                      <p style={{ color: '#8053D7' }}>
                        <a href=''>
                          <PlusCircleFilled style={{ color: '#8053D7', marginRight: 5 }} />
                        </a>
                        <FormattedMessage id="profile.main.learning-styles.addLearnerStyle"/>
                      </p>
                    </Col>
                    <Col xl={16} />
                    <Col xl={8}>
                      <Row gutter={[16, 16]}>
                        <Col>
                          <img src={Auditory} width='150px' style={{ display: 'block', margin: '0 auto' }} alt={intl.formatMessage({ id: 'profile.main.learning-styles.auditory' })} lang={'es'} />
                        </Col>
                        <Col>
                          <img src={ReadingIcon} width='150px' style={{ display: 'block', margin: '0 auto' }} alt={intl.formatMessage({ id: 'profile.main.learning-styles.reading' })} lang={'es'}/>
                        </Col>
                        <Col>
                          <img src={HandsOn} width='150px' style={{ display: 'block', margin: '0 auto' }} alt={intl.formatMessage({ id: 'profile.main.learning-styles.handsOn' })} lang={'es'} />
                        </Col>
                        <Col>
                          <img src={VisualIcon} width='150px' style={{ display: 'block', margin: '0 auto' }} alt={intl.formatMessage({ id: 'profile.main.learning-styles.visual' })} lang={'es'} />
                        </Col>
                      </Row>
                    </Col>
                    <Col xl={16} />
                  </Row>
                  <Row>
                    <Col>
                      <strong><FormattedMessage id="profile.main.learning-styles.publicProfile"/></strong>
                      <p style={{ color: '#777' }}><FormattedMessage id="profile.main.learning-styles.profileHelper"/></p>
                    </Col>
                    <Col>
                      <Switch defaultChecked={false} onChange={onSwitch} disabled />
                    </Col>
                  </Row>
                  <br />
                  {/* values: <pre>{JSON.stringify(values, 0, 2)}</pre> */}
                  {/* DELETE ACCOUNT */}
                  <Row>
                    <Col>
                      <Button danger disabled>
                        <FormattedMessage id="profile.main.delete-account-button"/>
                      </Button>
                    </Col>
                  </Row>

                  <Col style={{ width: '100px' }}>
                    <DoneButton
                      htmlType='submit'
                      disabled={
                        submitting ||
                        ((hasSubmitErrors || hasValidationErrors) &&
                        !dirtySinceLastSubmit) ||
                      pristine
                      }
                      loading={isLoading || submitting}
                      block
                    >
                      <FormattedMessage id="profile.main.save-button"/>
                    </DoneButton>
                  </Col>
                </AForm>
              </div>
            );
          }}
        />
      )}
    </MakeAsyncFunction>
  );
};

export default withRouter(ProfileForm);
